import { motion } from 'framer-motion';
import { Bot, Loader2, Send, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const AIChat = ({ setErrorModal }) => {
    const [chatHistory, setChatHistory] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const chatContainerRef = useRef(null);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatHistory]);

    const handleSendMessage = async (messageText = userInput) => {
        if (!messageText.trim() && !userInput.trim()) return;
        const currentMessage = messageText.trim() || userInput.trim();

        setChatHistory(prev => [...prev, { role: 'user', text: currentMessage }]);
        setUserInput('');
        setIsLoading(true);
        setErrorModal({ isOpen: false, title: '', message: '' });

        try {
            const response = await fetch('https://portfolio-backend-fp3e.onrender.com/api/chat/message', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ message: currentMessage }),
            });

            const data = await response.json();

            if (response.ok) {
                setChatHistory(prev => [...prev, {
                    role: 'assistant',
                    text: data.response
                }]);
            } else {
                throw new Error(data.error || 'Failed to get response');
            }
        } catch (error) {
            console.error('Chat error:', error);
            const errorMessage = error.name === 'TypeError' && error.message.includes('fetch')
                ? "The AI service is starting up, please try again in a moment..."
                : "Sorry, I encountered an issue. Please try again.";

            setChatHistory(prev => [...prev, {
                role: 'assistant',
                text: errorMessage
            }]);

            setErrorModal({
                isOpen: true,
                title: 'Connection Error',
                message: 'Unable to connect to AI service. Using fallback response.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section id="ai-chat" className="section">
            <div className="container">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center justify-center p-3 rounded-full bg-primary-purple/10 text-primary-purple mb-6">
                        <Bot size={32} />
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">Ask My AI</h2>
                    <p className="text-gray-400">Powered by advanced LLMs to answer questions about my work.</p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-4xl mx-auto"
                >
                    <div className="glass-card border border-white/10 bg-black/40 overflow-hidden flex flex-col h-[600px]">

                        {/* Chat Area */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent" ref={chatContainerRef}>
                            {chatHistory.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-60">
                                    <Sparkles size={48} className="text-primary-purple mb-4 animate-pulse-slow" />
                                    <h3 className="text-xl font-bold text-white mb-2">Start a Conversation</h3>
                                    <p className="text-gray-400 max-w-md mb-8">
                                        Ask about my skills, projects, experience, or what I'm currently learning.
                                    </p>
                                    <div className="flex flex-wrap justify-center gap-3">
                                        {[
                                            "What are Tarush's key skills?",
                                            "Tell me about the AudioMind project.",
                                            "What is Tarush's experience with AWS?",
                                        ].map((q, i) => (
                                            <button
                                                key={i}
                                                onClick={() => handleSendMessage(q)}
                                                className="text-sm px-4 py-2 rounded-full border border-white/10 hover:bg-white/5 hover:border-primary-purple/50 transition-all text-gray-300"
                                            >
                                                {q}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                chatHistory.map((msg, idx) => (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        key={idx}
                                        className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                                    >
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'user' ? 'bg-white/10' : 'bg-primary-purple/20 text-primary-purple'
                                            }`}>
                                            {msg.role === 'user' ? <UserIcon /> : <Bot size={16} />}
                                        </div>
                                        <div className={`max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed ${msg.role === 'user'
                                            ? 'bg-primary-purple text-white rounded-tr-sm'
                                            : 'bg-white/5 text-gray-300 border border-white/5 rounded-tl-sm'
                                            }`}>
                                            {msg.text}
                                        </div>
                                    </motion.div>
                                ))
                            )}
                            {isLoading && (
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-primary-purple/20 text-primary-purple flex items-center justify-center shrink-0">
                                        <Bot size={16} />
                                    </div>
                                    <div className="bg-white/5 text-gray-300 border border-white/5 px-4 py-3 rounded-2xl rounded-tl-sm flex items-center gap-2">
                                        <Loader2 size={14} className="animate-spin" />
                                        <span className="text-xs">Processing...</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Input Area */}
                        <div className="p-4 border-t border-white/5 bg-white/[0.02]">
                            <form
                                onSubmit={e => {
                                    e.preventDefault();
                                    handleSendMessage();
                                }}
                                className="flex gap-3"
                            >
                                <input
                                    type="text"
                                    placeholder="Type your question..."
                                    value={userInput}
                                    onChange={e => setUserInput(e.target.value)}
                                    className="input flex-1 bg-black/20 border-white/10 focus:border-primary-purple/50 rounded-xl"
                                    disabled={isLoading}
                                />
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4 rounded-xl"
                                    disabled={isLoading || !userInput.trim()}
                                >
                                    <Send size={18} />
                                </button>
                            </form>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

const UserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
    </svg>
);

export default AIChat;
